import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
  addToast,
} from "@heroui/react"
import { Trans } from "@lingui/react/macro"
import { clsx } from "clsx"
import { Clipboard } from "lucide-react"
import { useMemo } from "react"

interface TableValue {
  name: string
  value?: React.ReactNode
}

function renderTableRow(entry: TableValue, valueClass?: string) {
  const { name, value } = entry

  return (
    <TableRow key={name + (typeof value === "string" ? value : "")}>
      <TableCell css="w-48 max-w-48 overflow-hidden text-ellipsis align-top font-medium">
        {name.length > 20 ? (
          <Tooltip content={name} delay={1000}>
            {name}
          </Tooltip>
        ) : (
          name
        )}
      </TableCell>
      <TableCell
        className={clsx(valueClass, "group")}
        css="relative max-w-64 text-wrap break-words pr-10"
        height={35}
      >
        {value === "" || value == null ? (
          <span css="italic opacity-40">
            <Trans>Empty</Trans>
          </span>
        ) : (
          <>
            <div className="scroll-fade" css="max-h-64">
              {value}
            </div>
            <Button
              isIconOnly
              css="absolute right-2 top-1/2 hidden -translate-y-1/2 group-hover:flex"
              variant="light"
              onPress={async () => {
                try {
                  await navigator.clipboard.writeText(value as string)
                  addToast({
                    title: "Copied to clipboard",
                    color: "success",
                  })
                } catch (e) {
                  addToast({
                    title: "Failed to copy to clipboard",
                    description: (e as Error).message,
                    color: "danger",
                  })
                }
              }}
            >
              <Clipboard size={15} />
            </Button>
          </>
        )}
      </TableCell>
    </TableRow>
  )
}

export function KVTableHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 css="mb-2 ml-1 text-sm font-medium uppercase tracking-widest opacity-50">
      {children}
    </h3>
  )
}

export function KVBox({
  className,
  title,
  children,
}: {
  className?: string
  title: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className={className} css="mb-6">
      <KVTableHeader>{title}</KVTableHeader>
      <div css="break-words rounded-large bg-content1 px-6 py-4 text-[0.95rem] shadow-small">
        {children}
      </div>
    </div>
  )
}

export function KVTable({
  "aria-label": ariaLabel,
  title,
  className,
  valueCellClassName,
  emptyContent,
  values,
  sort,
}: {
  "aria-label": string
  title: React.ReactNode
  className?: string
  valueCellClassName?: string
  emptyContent?: React.ReactNode
  values: TableValue[]
  sort?: boolean
}) {
  const sorted = useMemo(
    () => (sort ? values.sort((a, b) => a.name.localeCompare(b.name)) : values),
    [values, sort]
  )

  return (
    <div className={className}>
      {typeof title === "string" ? <KVTableHeader>{title}</KVTableHeader> : title}
      <Table hideHeader isStriped aria-label={ariaLabel}>
        <TableHeader>
          <TableColumn>Title</TableColumn>
          <TableColumn>Value</TableColumn>
        </TableHeader>
        <TableBody emptyContent={emptyContent}>
          {sorted.map(entry => renderTableRow(entry, valueCellClassName))}
        </TableBody>
      </Table>
    </div>
  )
}
